import { useState, type ReactNode } from 'react';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Copy,
  ExternalLink,
  MoreVertical,
  Pencil,
  Trash2,
} from 'lucide-react';
import { DataTable, type DataTableColumn } from '@/components/ui/data-table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/components/utils/utils';
import { useI18n } from '@/hooks/useI18n';
import {
  EmailTemplate,
  EmailTemplateChannel,
  getChannelRecipients,
  isChannelEnabled,
} from '@/lib/admin/email-templates/models/email-template.model';
import {
  EmailTemplateSort,
  EmailTemplateSortKey,
} from '@/lib/admin/email-templates/utils/email-template-filters';

interface EmailTemplatesTableProps {
  templates: EmailTemplate[];
  channel: EmailTemplateChannel;
  isLoading: boolean;
  sort: EmailTemplateSort;
  onSortChange: (key: EmailTemplateSortKey) => void;
  onEdit: (template: EmailTemplate) => void;
  onDuplicate: (template: EmailTemplate) => void;
  onDelete: (key: string) => void;
  emptyState: ReactNode;
}

/**
 * Tabella dei template, con colonne diverse per canale (email vs notifica).
 * Ordinamento e ricerca sono governati dal parent: qui si rendono solo gli header
 * cliccabili che richiedono il cambio di chiave/direzione.
 */
export const EmailTemplatesTable = ({
  templates,
  channel,
  isLoading,
  sort,
  onSortChange,
  onEdit,
  onDuplicate,
  onDelete,
  emptyState,
}: EmailTemplatesTableProps) => {
  const { t, language } = useI18n();
  const [templateToDelete, setTemplateToDelete] = useState<EmailTemplate | null>(null);

  const formatDate = (value: string | null): string => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString(language, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const sortableHeader = (key: EmailTemplateSortKey, label: string) => {
    const isActive = sort.key === key;
    const Icon = !isActive ? ArrowUpDown : sort.direction === 'asc' ? ArrowUp : ArrowDown;
    return (
      <button
        type="button"
        onClick={() => onSortChange(key)}
        className={cn(
          'inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wide',
          isActive ? 'text-gray-900' : 'text-gray-500 hover:text-gray-700'
        )}
      >
        <span className="truncate">{label}</span>
        <Icon className="h-3 w-3 shrink-0" />
      </button>
    );
  };

  const recipientsCell = (template: EmailTemplate) => {
    const recipients = getChannelRecipients(template, channel);
    if (recipients.length === 0) {
      return <span className="text-xs italic text-gray-400">{t('emailTemplates.table.noRecipients')}</span>;
    }
    return (
      <div className="flex flex-wrap gap-1">
        {recipients.map((recipient) => (
          <Badge key={recipient} variant="secondary" className="text-xs">
            {recipient}
          </Badge>
        ))}
      </div>
    );
  };

  const statusCell = (template: EmailTemplate) => {
    const enabled = isChannelEnabled(template, channel);
    return (
      <Badge variant={enabled ? 'success' : 'outline'} className="text-xs">
        {enabled ? t('emailTemplates.table.statusActive') : t('emailTemplates.table.statusInactive')}
      </Badge>
    );
  };

  const linkCell = (url: string | null) => {
    if (!url) return <span className="text-xs italic text-gray-400">—</span>;
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(event) => event.stopPropagation()}
        className="inline-flex max-w-full items-center gap-1 text-xs text-blue-600 hover:underline"
      >
        <span className="truncate">{url}</span>
        <ExternalLink className="h-3 w-3 shrink-0" />
      </a>
    );
  };

  const keyColumn: DataTableColumn<EmailTemplate> = {
    id: 'template_key',
    header: sortableHeader('template_key', t('emailTemplates.fields.templateKey')),
    headClassName: 'w-[20%]',
    cell: (template) => (
      <span className="block truncate font-mono text-xs text-gray-900" title={template.template_key}>
        {template.template_key}
      </span>
    ),
  };

  /**
   * Il contenuto principale del canale: l'oggetto per le email, il testo del
   * messaggio per le notifiche (a DB non esiste un titolo separato per le
   * notifiche: `notification_message` è l'unico campo di testo).
   */
  const contentColumn: DataTableColumn<EmailTemplate> = {
    id: 'content',
    header: sortableHeader(
      'content',
      channel === 'email'
        ? t('emailTemplates.fields.subject')
        : t('emailTemplates.fields.notificationMessage')
    ),
    headClassName: 'w-[26%]',
    cell: (template) => {
      const value = channel === 'email' ? template.subject : template.notification_message;
      return (
        <span className="block truncate text-sm text-gray-900" title={value ?? ''}>
          {value || '—'}
        </span>
      );
    },
  };

  const statusColumn: DataTableColumn<EmailTemplate> = {
    id: 'status',
    header: sortableHeader('status', t('emailTemplates.table.status')),
    headClassName: 'w-[12%]',
    cell: statusCell,
  };

  /**
   * Date accorpate in una colonna sola: quella che conta è l'ultima modifica,
   * la creazione resta come riga secondaria di contesto.
   */
  const datesColumn: DataTableColumn<EmailTemplate> = {
    id: 'dates',
    header: sortableHeader('updated_at', t('emailTemplates.table.updatedAt')),
    headClassName: 'w-[14%]',
    cell: (template) => (
      <div className="leading-tight">
        <span className="block text-xs text-gray-700">{formatDate(template.updated_at)}</span>
        <span className="block text-[11px] text-gray-400">
          {t('emailTemplates.table.createdAtShort', { date: formatDate(template.created_at) })}
        </span>
      </div>
    ),
  };

  const columns: DataTableColumn<EmailTemplate>[] =
    channel === 'email'
      ? [
          keyColumn,
          contentColumn,
          {
            id: 'recipients',
            header: t('emailTemplates.fields.recipients'),
            headClassName: 'w-[16%]',
            cell: recipientsCell,
          },
          statusColumn,
          {
            id: 'mailgun_draft_link',
            header: t('emailTemplates.fields.mailgunDraftLink'),
            headClassName: 'w-[16%]',
            cell: (template) => linkCell(template.mailgun_draft_link),
          },
          datesColumn,
        ]
      : [
          keyColumn,
          contentColumn,
          {
            id: 'notification_recipients',
            header: t('emailTemplates.fields.notificationRecipients'),
            headClassName: 'w-[16%]',
            cell: recipientsCell,
          },
          statusColumn,
          {
            id: 'redirect_url',
            header: t('emailTemplates.fields.redirectUrl'),
            headClassName: 'w-[16%]',
            cell: (template) => linkCell(template.redirect_url),
          },
          datesColumn,
        ];

  const confirmDelete = () => {
    if (templateToDelete) onDelete(templateToDelete.template_key);
    setTemplateToDelete(null);
  };

  return (
    <>
      <DataTable<EmailTemplate>
        data={templates}
        columns={columns}
        getRowId={(template) => template.template_key}
        onRowClick={onEdit}
        paginated={false}
        // table-fixed comprime le colonne: su mobile serve una larghezza minima che
        // attivi lo scroll orizzontale, su desktop il 100% resta invariato.
        tableClassName="min-w-[980px]"
        isLoading={isLoading}
        emptyState={emptyState}
        actionsHeader={t('common.actions')}
        actionsHeadClassName="w-[64px]"
        renderActions={(template) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onEdit(template)}>
                <Pencil className="mr-2 h-4 w-4" />
                {t('common.edit')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onDuplicate(template)}>
                <Copy className="mr-2 h-4 w-4" />
                {t('common.duplicate')}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setTemplateToDelete(template)}
                className="text-red-600"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {t('common.delete')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      />

      <AlertDialog
        open={templateToDelete !== null}
        onOpenChange={(open) => {
          if (!open) setTemplateToDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('emailTemplates.delete.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('emailTemplates.delete.description', {
                key: templateToDelete?.template_key ?? '',
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
